import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal, Row, Form, Col, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import BasicTable from "../../components/UiComponent/customTable";
import { get_project_job } from "../../services/projectApi";
import {
  get_task_target,
  RegisterTaskTarget,
  UpdateTaskTarget,
} from "../../services/employeeApi";
import { GetUserInfo, hasAccess } from "../../utils/permissionChecker";
import { formatDate } from "../../utils/date_utils";

const initialFormState = {
  task_id: "",
  start_date: "",
  end_date: "",
  daily_target_quantity: "",
};

const TaskTarget = () => {
  const { t } = useTranslation();
  const user = GetUserInfo();
  const queryClient = useQueryClient();

  const currentUser = `${user.employee.first_name} ${user.employee.father_name}`;
  const today = new Date();

  const [showModal, setShowModal] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [formState, setFormState] = useState(initialFormState);
  const [formError, setFormError] = useState("");

  /* -------------------- Queries -------------------- */
  const { data: jobData = [], isLoading: jobsLoading } = useQuery(
    ["get_all_project_jobs_targets"],
    get_project_job
  );

  const {
    data: taskTargetData,
    isLoading: taskLoading,
    isError,
    error,
  } = useQuery(["get_employee_task_target"], get_task_target);

  /* -------------------- Mutation -------------------- */
  const taskTargetMutation = useMutation({
    mutationFn: (payload) =>
      isUpdateMode ? UpdateTaskTarget(payload) : RegisterTaskTarget(payload),
    onSuccess: (res) => {
      toast.success(res.message);
      queryClient.invalidateQueries(["get_employee_task_target"]);
      handleCloseModal();
    },
    onError: (err) => {
      toast.error(err.message || "Operation failed");
    },
  });

  /* -------------------- Derived Data -------------------- */
  const filteredTargets = useMemo(() => {
    const targets = taskTargetData?.data || [];
    if (["administrator", "manager"].includes(user.accessRole)) {
      return targets;
    }
    return targets.filter((t) => new Date(t.end_date) >= today);
  }, [taskTargetData, user.accessRole]);

  /* -------------------- Handlers -------------------- */
  const handleCloseModal = () => {
    setShowModal(false);
    setIsUpdateMode(false);
    setTargetId(null);
    setFormState(initialFormState);
    setFormError("");
  };

  const handleUpdate = useCallback((data) => {
    setFormState({
      task_id: data.task_id,
      start_date: data.start_date,
      end_date: data.end_date,
      daily_target_quantity: data.daily_target_quantity,
    });
    setTargetId(data.id);
    setIsUpdateMode(true);
    setShowModal(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formState.task_id) {
      setFormError("All fields are required");
      return;
    }

    const payload = {
      ...formState,
      measurement_type: "quantity",
      ...(isUpdateMode
        ? { id: targetId, updated_by: currentUser }
        : { created_by: currentUser }),
    };

    await taskTargetMutation.mutateAsync(payload);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      { header: "Job Title", accessorKey: "task_title" },
      {
        header: "Start Date",
        accessorFn: (row) => formatDate(row.start_date),
      },
      {
        header: "End Date",
        accessorFn: (row) => formatDate(row.end_date),
      },
      { header: "Measurement", accessorKey: "measurement_type" },
      { header: "Target", accessorKey: "target_type" },
      { header: "Quantity", accessorKey: "daily_target_quantity" },
    ];

    if (hasAccess(["administrator"], user.accessRole)) {
      baseColumns.push({
        header: "Action",
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="info"
            onClick={() => handleUpdate(row.original)}
          >
            {t("update")}
          </Button>
        ),
      });
    }

    return baseColumns;
  }, [handleUpdate, t, user.accessRole]);

  /* -------------------- Loading / Error -------------------- */
  if (jobsLoading || taskLoading) return <div>Loading...</div>;
  if (isError) return <div>Error: {error.message}</div>;

  /* -------------------- Render -------------------- */
  return (
    <>
      <BasicTable
        data={filteredTargets}
        columns={columns}
        title="Task Targets"
        actionButton={
          hasAccess(["administrator"], user.accessRole) && (
            <Button size="sm" variant="info" onClick={() => setShowModal(true)}>
              {t("add_new_Target")}
            </Button>
          )
        }
      />

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isUpdateMode ? "Update Target" : "Register New Target"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>{t("task_name")}</Form.Label>
                  <Form.Control
                    as="select"
                    value={formState.task_id}
                    onChange={(e) =>
                      setFormState({ ...formState, task_id: e.target.value })
                    }
                  >
                    <option value="" hidden>
                      Select Task
                    </option>
                    {jobData.map((job) => (
                      <option key={job.id} value={job.id}>
                        {job.job_title}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formState.start_date}
                    onChange={(e) =>
                      setFormState({ ...formState, start_date: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formState.end_date}
                    onChange={(e) =>
                      setFormState({ ...formState, end_date: e.target.value })
                    }
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Daily Target Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    value={formState.daily_target_quantity}
                    onChange={(e) =>
                      setFormState({
                        ...formState,
                        daily_target_quantity: e.target.value,
                      })
                    }
                  />
                </Form.Group>

                {formError && <p className="text-danger mt-2">{formError}</p>}
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button
            variant="info"
            onClick={handleSubmit}
            disabled={taskTargetMutation.isLoading}
          >
            {taskTargetMutation.isLoading ? (
              <Spinner size="sm" animation="border" />
            ) : isUpdateMode ? (
              "Update"
            ) : (
              "Register"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TaskTarget;
